import { Button, Toolbar, Typography } from "@mui/material";

import { useToast } from "@qubit/autoparts";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import InventoryAdjustDialog, {
  InventoryToModify
} from "~/features/inventoryAdjustDialog/InventoryAdjustDialog";
import { InventoryHoldModal } from "~/features/inventoryHoldModal/InventoryHoldModal";
import { NavBarButtonGroup, UnselectButton } from "~/features/navbar/Navbar";
import {
  generateLocationNameFromBin,
  getHoldTypeOptions,
  getInventoryDateObj,
  HoldType
} from "~/lib/helpers";
import { mixpanelTrack, useMixpanelContextName } from "~/lib/mixpanel-tracking";
import {
  fetchCycleCountAndBins,
  fetchCycleCountBins,
  postBinCount
} from "~/redux/actions/cycleCounts";
import {
  getInventoryReport,
  placeInventoryHold
} from "~/redux/actions/inventory";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectWorkstationId } from "~/redux/selectors/workstationsSelectors";

import { selectCycleCountBinId } from "./cycleCounts.slice";

export function CycleCountToolbar() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast, successToast } = useToast();
  const trackedPageName = useMixpanelContextName("Toolbar");

  const { ap_excludeRecalledHold, inv_inventoryDateLabel } =
    useAppSelector(selectServerConfig);
  const cycleCount = useAppSelector((state) => state.cycleCounts.cycleCount);
  const selectedCycleCountBinId = useAppSelector(
    (state) => state.cycleCountsSlice.selectedCycleCountBinId
  );
  const selectedInventory = useAppSelector(
    (state) => state.cycleCountsSlice.selectedInventory
  );
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const thisWorkstationId = useAppSelector(selectWorkstationId);
  const [isAdjustPanelOpen, setAdjustPanelOpen] = useState(false);
  const [isBinHoldModalOpen, setIsBinHoldModalOpen] = useState(false);

  const handleVerify = useCallback(async () => {
    if (
      cycleCount &&
      selectedInventory &&
      selectedInventory.count &&
      selectedCycleCountBinId
    ) {
      mixpanelTrack({
        trackedPageName,
        label: "Verify",
        type: "Button Click",
        eventProperties: {
          "Verified Count": selectedInventory.count.value,
          "Bin ID": selectedInventory.bin.binId,
          "Variant ID": selectedInventory.variantId
        }
      });
      await dispatch(
        postBinCount({
          verifiedCount: selectedInventory.count,
          binId: selectedCycleCountBinId,
          inventoryId: selectedInventory.inventoryId,
          cycleCountId: cycleCount?.cycleCountId
        })
      );

      if (selectedVariant) {
        await dispatch(getInventoryReport(selectedVariant.variantId));
      }

      successToast("Verified", {
        description: `${selectedVariant?.productName ? selectedVariant?.productName + " " : ""}${
          selectedInventory.count.value
        } ${selectedInventory.count.units}`
      });
    } else {
      errorToast("No inventory selected");
    }
  }, [
    cycleCount,
    dispatch,
    errorToast,
    selectedCycleCountBinId,
    selectedInventory,
    selectedVariant,
    successToast,
    trackedPageName
  ]);

  let selectedLocationName = "";
  if (selectedInventory) {
    selectedLocationName =
      selectedInventory.bin.binType === "autostore"
        ? "Autostore"
        : generateLocationNameFromBin(selectedInventory.bin);
  }

  const selectedInventoryToModify: InventoryToModify = selectedInventory
    ? {
        binType: selectedInventory.bin.binType,
        binId: selectedInventory.bin.binId,
        inventoryId: selectedInventory.inventoryId,
        locationName: selectedLocationName,
        count: selectedInventory.count,
        committedCount: selectedInventory.committedCount,
        ...getInventoryDateObj(
          inv_inventoryDateLabel,
          dayjs(selectedInventory?.expiration) ||
            dayjs(selectedInventory?.manufactureDate)
        ),
        lotNumber: selectedInventory.lotNumber,
        variantId: selectedInventory.variantId
      }
    : null;

  return (
    <Toolbar>
      <UnselectButton
        onClick={() => {
          mixpanelTrack({
            trackedPageName,
            label: "Deselect Bin Row",
            type: "Button Click"
          });
          dispatch(selectCycleCountBinId(null));
        }}
      />
      <NavBarButtonGroup>
        <Button
          color="secondary"
          onClick={handleVerify}
          style={{
            width: 175
          }}
        >
          <Typography variant="body2" style={{ color: "#fff" }}>
            {t("verify")}
          </Typography>
        </Button>

        <Button
          color="secondary"
          onClick={() => {
            mixpanelTrack({
              trackedPageName,
              label: "Adjust Inventory",
              type: "Button Click"
            });
            setAdjustPanelOpen(true);
          }}
          style={{
            width: 175
          }}
        >
          <Typography variant="body2" style={{ color: "#fff" }}>
            {t("adjust")}
          </Typography>
        </Button>
        <Button
          color="secondary"
          onClick={() => {
            mixpanelTrack({
              trackedPageName,
              label: "Inventory Holds",
              type: "Button Click"
            });
            setIsBinHoldModalOpen(true);
          }}
          style={{
            width: 175
          }}
        >
          <Typography variant="body2" style={{ color: "#fff" }}>
            {t("nav.link.inventory holds")}
          </Typography>
        </Button>
      </NavBarButtonGroup>
      <InventoryAdjustDialog
        open={isAdjustPanelOpen}
        onClose={() => {
          setAdjustPanelOpen(false);
          dispatch(selectCycleCountBinId(null));
        }}
        invToModify={selectedInventoryToModify}
        selectedVariant={selectedVariant || null}
        refreshCb={async () => {
          if (cycleCount) {
            await dispatch(fetchCycleCountBins([cycleCount.variant.variantId]));
          }
          setAdjustPanelOpen(false);
        }}
        canceledReason=""
      />
      <InventoryHoldModal
        confirmCb={async (holdBin: HoldType) => {
          const { reasonCode } = holdBin;
          await dispatch(
            placeInventoryHold(
              selectedInventory!.inventoryId,
              reasonCode,
              thisWorkstationId === undefined ? null : thisWorkstationId
            )
          );

          setIsBinHoldModalOpen(false);
          if (selectedVariant && cycleCount) {
            await dispatch(fetchCycleCountAndBins(cycleCount.cycleCountId));
          }
        }}
        open={isBinHoldModalOpen}
        closeModalCallback={() => setIsBinHoldModalOpen(false)}
        maxWidth="sm"
        options={getHoldTypeOptions({
          excludeRecalledHold: ap_excludeRecalledHold
        })}
        confirmButtonText={t("set hold")}
        confirmActionTypeForTracking="Set Hold"
        currentHold=""
      />
    </Toolbar>
  );
}
